import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  RefreshCw,
  Save,
  Trash2,
  Wallet,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api, type SystemInfo } from "../lib/api";
import { AssinaturasCard } from "./AssinaturasCard";
import { WhatsappUsoCard } from "./WhatsappUsoCard";

interface Props {
  onVoltar: () => void;
}

interface ExtratoItem {
  condominio_id: number | null;
  condominio_nome: string;
  paginas: number;
  tokens_entrada: number;
  tokens_saida: number;
  custo_usd: number;
  custo_brl: number;
  orcamento_brl: number | null;
  orcamento_especifico: boolean;
  saldo_brl: number | null;
  percentual_usado: number | null;
  paginas_restantes: number | null;
}
interface OrcamentoCond {
  condominio_id: number;
  nome: string;
  codigo?: string;
  orcamento_brl: number | null;
  api_propria?: boolean;
  valor_contratado_brl?: number | null;
}
interface OrcamentosCondResp {
  itens: OrcamentoCond[];
  padrao_brl: number;
  sou_dono?: boolean;
}
interface Extrato {
  ano: number;
  mes: number;
  itens: ExtratoItem[];
  total_paginas: number;
  total_tokens_entrada: number;
  total_tokens_saida: number;
  total_usd: number;
  total_brl: number;
  orcamento_por_condominio_brl: number;
  condominios_no_limite: number;
  usd_brl: number;
}
interface ConfigUso {
  orcamento_mensal_brl: number;
  usd_brl: number;
  usd_brl_auto?: number;   // 1 = busca a cotação do dia automaticamente
  usd_brl_epoch?: number;  // quando a cotação foi buscada por último (unix ts)
  preco_input_usd_mtok: number;
  preco_output_usd_mtok: number;
  conta_propria?: number;  // 1 = cliente usa a própria chave de API
}
const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function UsoPage({ onVoltar }: Props) {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const qc = useQueryClient();

  const { data: ext } = useQuery<Extrato>({
    queryKey: ["uso-extrato", ano, mes],
    queryFn: () => api.get<Extrato>(`/uso/extrato?ano=${ano}&mes=${mes}`),
  });
  const { data: cfg } = useQuery<ConfigUso>({
    queryKey: ["uso-config"],
    queryFn: () => api.get<ConfigUso>("/uso/config"),
  });
  const { data: info } = useQuery<SystemInfo>({
    queryKey: ["info"],
    queryFn: () => api.get<SystemInfo>("/info"),
  });
  const ehDev = info?.env === "dev";

  // Orçamento (teto de OCR) por condomínio — guardado no banco.
  const { data: orcs } = useQuery<OrcamentosCondResp>({
    queryKey: ["uso-orcamentos-cond"],
    queryFn: () => api.get<OrcamentosCondResp>("/uso/orcamentos-cond"),
  });
  // Valores em edição por condomínio (string do input). "" = usar padrão.
  const [orcEdit, setOrcEdit] = useState<Record<number, string>>({});
  useEffect(() => {
    if (orcs) {
      const m: Record<number, string> = {};
      for (const o of orcs.itens) m[o.condominio_id] = o.orcamento_brl == null ? "" : String(o.orcamento_brl);
      setOrcEdit(m);
    }
  }, [orcs]);
  const salvarOrcCond = useMutation({
    mutationFn: (v: { condominio_id: number; orcamento_brl: number | null }) =>
      api.patch<OrcamentoCond>("/uso/orcamentos-cond", v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["uso-orcamentos-cond"] });
      qc.invalidateQueries({ queryKey: ["uso-extrato"] });
    },
  });
  const [salvoId, setSalvoId] = useState<number | null>(null);
  const aplicarOrc = (id: number) => {
    const raw = (orcEdit[id] ?? "").trim().replace(",", ".");
    const valor = raw === "" ? null : Number(raw);
    if (valor !== null && (Number.isNaN(valor) || valor < 0)) return;
    salvarOrcCond.mutate(
      { condominio_id: id, orcamento_brl: valor },
      { onSuccess: () => { setSalvoId(id); setTimeout(() => setSalvoId(null), 2000); } },
    );
  };

  // Só o Dono edita teto e valor contratado; o síndico apenas consulta.
  const souDono = orcs?.sou_dono ?? false;

  // Valor CONTRATADO de API (só para condomínios com chave própria) — Dono informa.
  const [valEdit, setValEdit] = useState<Record<number, string>>({});
  useEffect(() => {
    if (orcs) {
      const m: Record<number, string> = {};
      for (const o of orcs.itens) m[o.condominio_id] = o.valor_contratado_brl == null ? "" : String(o.valor_contratado_brl);
      setValEdit(m);
    }
  }, [orcs]);
  const salvarValContratado = useMutation({
    mutationFn: (v: { condominio_id: number; orcamento_brl: number | null }) =>
      api.patch("/uso/valor-contratado-cond", v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["uso-orcamentos-cond"] }),
  });
  const [salvoValId, setSalvoValId] = useState<number | null>(null);
  const aplicarVal = (id: number) => {
    const raw = (valEdit[id] ?? "").trim().replace(",", ".");
    const valor = raw === "" ? null : Number(raw);
    if (valor !== null && (Number.isNaN(valor) || valor < 0)) return;
    salvarValContratado.mutate(
      { condominio_id: id, orcamento_brl: valor },
      { onSuccess: () => { setSalvoValId(id); setTimeout(() => setSalvoValId(null), 2000); } },
    );
  };

  const limparCache = useMutation({
    mutationFn: () => api.post<{ removidos: number }>("/uso/limpar-cache"),
    onSuccess: (r) =>
      window.alert(
        `Cache de OCR limpo (${r.removidos} itens). O próximo processamento vai reler ` +
          "pelo Vision (custo real) — útil para testar a medição.",
      ),
  });
  const confirmarLimpar = () => {
    if (
      window.confirm(
        "Limpar o cache de OCR? O próximo processamento de QUALQUER mês vai reler " +
          "pelo Vision e gerar custo real. Use só para teste.",
      )
    )
      limparCache.mutate();
  };

  // Obs.: a limpeza SELETIVA de OCR (por mês) fica na tela do condomínio, ao
  // lado de Reprocessar — lá o mês é explícito e sempre disponível. Aqui o
  // extrato é por mês de FATURA (quando o Vision rodou), que pode diferir do
  // mês do balancete, então não acoplamos a limpeza a estas linhas.

  // Form de configuração (orçamento + câmbio)
  const [orc, setOrc] = useState("");
  const [cambio, setCambio] = useState("");
  const [cambioAuto, setCambioAuto] = useState(true);
  useEffect(() => {
    if (cfg) {
      setOrc(String(cfg.orcamento_mensal_brl ?? 0));
      setCambio(String(cfg.usd_brl ?? 0));
      setCambioAuto((cfg.usd_brl_auto ?? 1) >= 0.5);
    }
  }, [cfg]);

  const salvar = useMutation({
    mutationFn: () =>
      api.patch<ConfigUso>("/uso/config", {
        orcamento_mensal_brl: Number(orc) || 0,
        usd_brl: Number(cambio) || cfg?.usd_brl,
        usd_brl_auto: cambioAuto ? 1 : 0,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["uso-config"] });
      qc.invalidateQueries({ queryKey: ["uso-extrato"] });
    },
  });

  // Busca a cotação USD->BRL do dia (custo zero).
  const [cambioMsg, setCambioMsg] = useState<string | null>(null);
  const atualizarCambio = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; usd_brl: number; fonte?: string; motivo?: string }>(
        "/uso/cambio/atualizar"),
    onSuccess: (r) => {
      if (r.usd_brl) setCambio(String(r.usd_brl));
      setCambioMsg(r.ok ? `Cotação de hoje: US$ 1 = R$ ${r.usd_brl} (${r.fonte ?? "web"})`
                        : (r.motivo ?? "Não consegui buscar a cotação."));
      qc.invalidateQueries({ queryKey: ["uso-config"] });
      qc.invalidateQueries({ queryKey: ["uso-extrato"] });
      qc.invalidateQueries({ queryKey: ["uso-saldo-conta"] });
    },
    onError: () => setCambioMsg("Não consegui buscar a cotação (sem internet?)."),
  });

  const irMes = (delta: number) => {
    let m = mes + delta;
    let a = ano;
    if (m < 1) { m = 12; a -= 1; }
    if (m > 12) { m = 1; a += 1; }
    setMes(m); setAno(a);
  };

  const exportarCsv = () => {
    if (!ext) return;
    const sep = ";";
    const linhas = [
      ["Condomínio", "Páginas", "Tokens entrada", "Tokens saída", "Custo US$", "Custo R$"],
      ...ext.itens.map((i) => [
        i.condominio_nome, i.paginas, i.tokens_entrada, i.tokens_saida,
        i.custo_usd.toFixed(4), i.custo_brl.toFixed(2),
      ]),
      ["TOTAL", ext.total_paginas, ext.total_tokens_entrada ?? "", ext.total_tokens_saida ?? "",
        ext.total_usd.toFixed(2), ext.total_brl.toFixed(2)],
    ];
    const csv = linhas.map((l) => l.join(sep)).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `uso-${ano}-${String(mes).padStart(2, "0")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const corSaldo = (p: number | null) =>
    p == null ? "text-slate-900" : p >= 100 ? "text-rose-600" : p >= 80 ? "text-amber-600" : "text-emerald-700";

  return (
    <div>
      <button
        type="button"
        onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} />
        Voltar
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Wallet size={22} className="text-brand-700" />
        <h2 className="text-2xl font-semibold text-slate-900">Uso &amp; Custos do OCR</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Custo <b>estimado</b> da leitura automática (Claude Vision) por condomínio.
        O valor oficial fica no painel da Anthropic; aqui é o controle fino.
      </p>

      {/* Assinatura do serviço por condomínio (0.1.86) — o mais importante, no topo. */}
      <AssinaturasCard />

      {/* Navegador de mês */}
      <div className="flex items-center gap-2 mb-4">
        <button type="button" onClick={() => irMes(-1)}
          className="p-1.5 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-slate-900">
          <ChevronLeft size={18} />
        </button>
        <span className="text-sm font-semibold text-slate-800 w-40 text-center">
          {MESES[mes - 1]} / {ano}
        </span>
        <button type="button" onClick={() => irMes(1)}
          className="p-1.5 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-slate-900">
          <ChevronRight size={18} />
        </button>
        <button type="button" onClick={exportarCsv} disabled={!ext || ext.itens.length === 0}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:border-brand-400 disabled:opacity-40 text-slate-700 text-xs font-medium rounded-md">
          <Download size={14} /> Exportar CSV
        </button>
      </div>

      {/* Cartões */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Card titulo="Gasto no mês (todos)" valor={ext ? brl(ext.total_brl) : "—"}
          sub={ext ? `${ext.total_paginas} páginas lidas · US$ ${ext.total_usd.toFixed(2)}` : ""} />
        <Card titulo="Orçamento por condomínio" valor={cfg && cfg.orcamento_mensal_brl > 0 ? brl(cfg.orcamento_mensal_brl) : "sem limite"}
          sub="cada condomínio é independente" />
        <Card titulo="Condomínios no limite" valor={ext ? String(ext.condominios_no_limite) : "—"}
          sub={ext && ext.condominios_no_limite > 0 ? "novas leituras bloqueadas neles" : ""} />
      </div>

      {/* Tabela por condomínio */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
              <th className="text-left font-semibold px-4 py-2">Condomínio</th>
              <th className="text-right font-semibold px-4 py-2">Páginas</th>
              <th className="text-right font-semibold px-4 py-2">Custo (R$)</th>
              <th className="text-right font-semibold px-4 py-2">Saldo (R$)</th>
            </tr>
          </thead>
          <tbody>
            {ext && ext.itens.length > 0 ? (
              ext.itens.map((i) => (
                <tr key={String(i.condominio_id)} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-800">
                    {i.condominio_nome}
                    {i.orcamento_especifico && (
                      <span className="ml-2 align-middle text-[10px] font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded px-1.5 py-0.5">
                        teto próprio{i.orcamento_brl != null ? ` ${brl(i.orcamento_brl)}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{i.paginas}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium text-slate-900">{brl(i.custo_brl)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums font-medium ${corSaldo(i.percentual_usado)}`}>
                    {i.saldo_brl != null ? brl(i.saldo_brl) : "—"}
                    {i.paginas_restantes != null && (
                      <span className="block text-[11px] font-normal text-slate-400">
                        ≈ {i.paginas_restantes} págs
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                Nenhuma leitura paga neste mês.
              </td></tr>
            )}
          </tbody>
          {ext && ext.itens.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50">
                <td className="px-4 py-2 font-semibold text-slate-700">Total</td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold">{ext.total_paginas}</td>
                <td className="px-4 py-2 text-right tabular-nums font-bold text-slate-900">{brl(ext.total_brl)}</td>
                <td className="px-4 py-2"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* WhatsApp (0.1.87): mensagens do mês e custo estimado. */}
      <WhatsappUsoCard ano={ano} mes={mes} />

      {/* Teto de leitura (OCR) por condomínio — só o Dono edita.
          Condomínio com API própria: some o teto e mostra o valor contratado. */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">
          Teto de leitura (OCR) por condomínio
        </h3>
        <p className="text-[11px] text-slate-500 mb-3">
          {souDono
            ? <>Defina o limite mensal de cada condomínio e clique em Salvar. Quando um
              condomínio atinge o teto no mês, só ele para de fazer novas leituras pagas.
              Deixe em branco para usar o padrão
              {orcs ? ` (${orcs.padrao_brl > 0 ? brl(orcs.padrao_brl) : "sem limite"})` : ""};
              use 0 para “sem limite”.</>
            : <>O teto de leitura é definido pelo administrador (Dono). Aqui você
              apenas consulta o limite do seu condomínio.</>}
        </p>
        {orcs && orcs.itens.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {orcs.itens.map((o) => (
              <li key={o.condominio_id} className="flex items-center gap-3 py-2 flex-wrap">
                <span className="flex-1 min-w-[180px] text-sm text-slate-800">
                  {o.nome}
                  {o.api_propria && (
                    <span className="ml-2 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                      API própria{o.valor_contratado_brl != null ? ` · contratado ${brl(o.valor_contratado_brl)}` : ""}
                    </span>
                  )}
                  {o.codigo && (
                    <span className="block text-[10px] text-slate-400 font-mono">
                      Código: {o.codigo}
                    </span>
                  )}
                </span>

                {o.api_propria ? (
                  // API PRÓPRIA: sem teto do Dono; mostra/edita o valor contratado.
                  souDono ? (
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] text-slate-400">contratado R$</span>
                      <input
                        type="number" min={0} step="1"
                        value={valEdit[o.condominio_id] ?? ""}
                        placeholder="informe"
                        onChange={(e) => setValEdit((m) => ({ ...m, [o.condominio_id]: e.target.value }))}
                        className="w-24 px-2 py-1 border border-slate-300 rounded-md text-sm text-right"
                      />
                      <button type="button" onClick={() => aplicarVal(o.condominio_id)}
                        disabled={salvarValContratado.isPending}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-xs font-medium rounded-md">
                        {salvoValId === o.condominio_id ? "Salvo ✓" : "Salvar"}
                      </button>
                    </div>
                  ) : (
                    <span className="text-[11px] text-slate-500">
                      {o.valor_contratado_brl != null ? `Contratado: ${brl(o.valor_contratado_brl)}` : "Valor contratado não informado"}
                    </span>
                  )
                ) : souDono ? (
                  // CHAVE DO DONO + Dono: teto editável.
                  <>
                    <span className="text-[11px] text-slate-400">
                      atual: {o.orcamento_brl == null ? "padrão" : o.orcamento_brl > 0 ? brl(o.orcamento_brl) : "sem limite"}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-slate-400">R$</span>
                      <input
                        type="number" min={0} step="1"
                        value={orcEdit[o.condominio_id] ?? ""}
                        placeholder="padrão"
                        onChange={(e) => setOrcEdit((m) => ({ ...m, [o.condominio_id]: e.target.value }))}
                        className="w-24 px-2 py-1 border border-slate-300 rounded-md text-sm text-right"
                      />
                    </div>
                    <button type="button" onClick={() => aplicarOrc(o.condominio_id)}
                      disabled={salvarOrcCond.isPending}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-xs font-medium rounded-md">
                      {salvoId === o.condominio_id ? "Salvo ✓" : "Salvar"}
                    </button>
                  </>
                ) : (
                  // CHAVE DO DONO + síndico: só lê o teto.
                  <span className="text-sm text-slate-600">
                    Teto: {o.orcamento_brl == null
                      ? (orcs.padrao_brl > 0 ? `${brl(orcs.padrao_brl)} (padrão)` : "sem limite (padrão)")
                      : o.orcamento_brl > 0 ? brl(o.orcamento_brl) : "sem limite"}
                  </span>
                )}

                {/* Lembrete: valor contratado é importante para acompanhar o consumo. */}
                {o.api_propria && souDono && o.valor_contratado_brl == null && (
                  <span className="w-full text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Informe o <b>valor contratado</b> deste condomínio — é o que permite acompanhar
                    quanto já foi usado e avisar quando estiver perto de acabar.
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">Nenhum condomínio cadastrado ainda.</p>
        )}
      </div>

      {/* Configuração (nível do Dono: orçamento padrão, câmbio, preços) — o síndico não edita. */}
      {souDono && (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 max-w-xl">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Configuração</h3>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-500 mb-1">Orçamento padrão por condomínio (R$) — usado quando o condomínio não tem teto próprio; 0 = sem limite</span>
            <input type="number" min={0} value={orc} onChange={(e) => setOrc(e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          </label>
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-500 mb-1">
              Câmbio US$ → R$
              {cfg?.usd_brl_epoch ? (
                <span className="text-slate-400"> · atualizado {new Date(cfg.usd_brl_epoch * 1000).toLocaleDateString("pt-BR")}</span>
              ) : null}
            </span>
            <div className="flex items-center gap-2">
              <input type="number" min={0} step="0.01" value={cambio} onChange={(e) => setCambio(e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
              <button type="button" onClick={() => atualizarCambio.mutate()} disabled={atualizarCambio.isPending}
                title="Buscar a cotação do dólar de hoje (sem custo)"
                className="shrink-0 inline-flex items-center gap-1 px-2 py-1.5 bg-white border border-slate-300 hover:border-brand-400 text-slate-700 text-xs rounded-md disabled:opacity-50">
                {atualizarCambio.isPending ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                Hoje
              </button>
            </div>
            <span className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
              <input type="checkbox" checked={cambioAuto} onChange={(e) => setCambioAuto(e.target.checked)} />
              Buscar automaticamente a cotação do dia
            </span>
            {cambioMsg && <span className="block text-[11px] text-slate-500 mt-1">{cambioMsg}</span>}
          </label>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button type="button" onClick={() => salvar.mutate()} disabled={salvar.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-medium rounded-md">
            {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Salvar
          </button>
          {salvar.isSuccess && <span className="text-xs text-emerald-700">Salvo.</span>}
          <span className="text-[11px] text-slate-400 ml-auto">
            Vale para CADA condomínio: quando um atinge o orçamento, só ele é bloqueado até o mês seguinte.
          </span>
        </div>
        {ehDev && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={confirmarLimpar}
              disabled={limparCache.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50 text-xs font-medium rounded-md"
            >
              {limparCache.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Trash2 size={13} />
              )}
              Limpar cache de OCR (DEV)
            </button>
            <span className="ml-2 text-[11px] text-slate-400">
              Força releitura paga no próximo processamento. Só aparece em desenvolvimento.
            </span>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function Card({ titulo, valor, sub }: { titulo: string; valor: string; sub?: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">{titulo}</p>
      <p className="text-xl font-bold text-slate-900 mt-1">{valor}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}
